import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { myLikedTracks, mySubscriptions, myLiked, myWatchLater, toggleTrackLike, toggleSubscribe, resolveFileUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';
import { Headphones, Heart, Film, Radio, Library, Music, User, Play, Pause, UserCheck, Loader2, LogOut } from 'lucide-react';
import PwaInstallButton from '../components/PwaInstallButton';

const TABS = [
  { id: 'tracks',  label: 'Liked tracks',    icon: Music },
  { id: 'videos',  label: 'Liked videos',    icon: Film },
  { id: 'artists', label: 'Following',       icon: UserCheck },
  { id: 'saved',   label: 'Watch later',     icon: Library },
];

function TrackRow({ t, allTracks, onUnlike }) {
  const { current, playing, playQueue, toggle } = usePlayer();
  const isCurrent = current?.id === t.id;
  const handlePlay = () => {
    if (isCurrent) { toggle(); return; }
    const idx = allTracks.findIndex(x => x.id === t.id);
    playQueue(allTracks, Math.max(0, idx));
  };
  return (
    <div className={`card-shell p-3 flex items-center gap-3 ${isCurrent ? 'border-[#a78bfa]/55' : ''}`} data-testid={`lp-track-${t.id}`}>
      <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-white/5 grid place-items-center">
        {t.cover_url ? <img src={resolveFileUrl(t.cover_url)} alt="" className="w-full h-full object-cover" /> : <Music className="w-6 h-6 text-white/40" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-[14.5px] truncate ${isCurrent ? 'text-[#a78bfa]' : ''}`}>{t.title}</div>
        <Link to={t.channel_id ? `/channel/${t.channel_id}` : '#'} className="text-[12px] text-white/60 hover:text-white truncate">{t.artist_name}</Link>
      </div>
      <button onClick={handlePlay} className="w-10 h-10 rounded-full grid place-items-center bg-white/10 border border-white/15 hover:bg-white/15" aria-label={isCurrent && playing ? 'Pause' : 'Play'}>
        {isCurrent && playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <button onClick={() => onUnlike(t)} className="w-10 h-10 rounded-full grid place-items-center bg-[#ff2e63]/20 border border-[#ff2e63]/40 hover:bg-[#ff2e63]/30" data-testid={`lp-unlike-${t.id}`}>
        <Heart className="w-4 h-4 fill-[#ff2e63] text-[#ff2e63]" />
      </button>
    </div>
  );
}

export default function ListenerProfile() {
  const { user, logout } = useApp();
  const [tab, setTab] = useState('tracks');
  const [tracks, setTracks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [subs, setSubs] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, v, s, w] = await Promise.all([
        myLikedTracks().catch(() => []),
        myLiked().catch(() => []),
        mySubscriptions().catch(() => []),
        myWatchLater().catch(() => []),
      ]);
      setTracks(t); setVideos(v); setSubs(s); setSaved(w);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const unlikeTrack = async (t) => {
    const prev = tracks;
    setTracks(list => list.filter(x => x.id !== t.id));
    try { await toggleTrackLike(t.id); toast.success('Removed'); }
    catch { setTracks(prev); toast.error('Could not remove'); }
  };

  const unfollow = async (ch) => {
    try {
      await toggleSubscribe(ch.id);
      setSubs(list => list.filter(x => x.id !== ch.id));
      toast.success(`Unfollowed ${ch.name}`);
    } catch { toast.error('Could not unfollow'); }
  };

  const stats = [
    { k: 'Liked tracks', v: tracks.length, c: '#a78bfa' },
    { k: 'Following',    v: subs.length,   c: '#22d3ee' },
    { k: 'Saved videos', v: saved.length,  c: '#ff2e63' },
  ];

  return (
    <div data-testid="listener-profile" className="max-w-[1100px] mx-auto">
      {/* Header card */}
      <div className="card-shell p-5 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-60 pointer-events-none" style={{ background: 'radial-gradient(1200px 300px at 80% -20%, rgba(34,211,238,0.18), transparent 60%), radial-gradient(900px 240px at 10% 120%, rgba(167,139,250,0.18), transparent 60%)' }} />
        <div className="relative flex flex-col md:flex-row md:items-start gap-5">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl grid place-items-center shrink-0" style={{ background: 'linear-gradient(135deg,#22d3ee,#a78bfa)' }}>
              {user?.avatar ? <img src={resolveFileUrl(user.avatar)} className="w-full h-full object-cover rounded-2xl" alt="" /> : <User className="w-7 h-7 md:w-8 md:h-8 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="chip active text-[10px] uppercase"><Headphones className="w-3 h-3 mr-1 inline" /> Listener</div>
              <h1 className="font-display text-[24px] sm:text-[28px] md:text-[34px] mt-2 leading-none truncate">{user?.name || 'You'}</h1>
              <div className="text-white/55 text-[12.5px] mt-1 truncate">{user?.email}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 md:gap-3 md:min-w-[280px]">
            {stats.map(s => (
              <div key={s.k} className="card-shell px-3 py-2.5 md:px-4 md:py-3 text-center" style={{ borderColor: s.c + '55' }}>
                <div className="font-display text-[20px] md:text-[24px] leading-none" style={{ color: s.c }}>{s.v}</div>
                <div className="text-[9.5px] md:text-[10.5px] text-white/60 uppercase tracking-wider mt-1">{s.k}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative flex flex-wrap gap-2 mt-5">
          <Link to="/feed" className="btn-primary text-[12.5px]"><Play className="w-3.5 h-3.5" /> Open reel</Link>
          <Link to="/artists" className="btn-ghost text-[12.5px]"><Music className="w-3.5 h-3.5" /> Browse artists</Link>
          <Link to="/hub" className="btn-ghost text-[12.5px]"><Library className="w-3.5 h-3.5" /> Knowledge Hub</Link>
          <PwaInstallButton variant="ghost" label="Install app" testid="listener-pwa-install" className="text-[12.5px]" />
          <button onClick={logout} className="btn-ghost text-[12.5px]" data-testid="lp-logout"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mt-6">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`chip ${tab === t.id ? 'active' : ''}`} data-testid={`lp-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5 mr-1 inline" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center text-white/50"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="mt-5">
          {tab === 'tracks' && (
            <div className="space-y-2">
              {tracks.length === 0 && <EmptyState icon={Music} label="Nothing liked yet." cta="Open the reel and tap the heart on tracks you love." to="/feed" btn="Open reel" />}
              {tracks.map(t => <TrackRow key={t.id} t={t} allTracks={tracks} onUnlike={unlikeTrack} />)}
            </div>
          )}
          {tab === 'videos' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.length === 0 && <div className="col-span-full"><EmptyState icon={Film} label="No liked videos yet." cta="Like music videos you enjoy and they'll show up here." to="/feed" btn="Browse reel" /></div>}
              {videos.map(v => (
                <Link key={v.id} to={`/watch/${v.id}`} className="card-shell p-0 overflow-hidden hover-lift" data-testid={`lp-video-${v.id}`}>
                  <div className="aspect-video bg-white/5 relative">
                    {v.thumb ? <img src={resolveFileUrl(v.thumb)} alt={v.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><Film className="w-8 h-8 text-white/30" /></div>}
                  </div>
                  <div className="p-3">
                    <div className="font-semibold text-[14px] line-clamp-2">{v.title}</div>
                    <div className="text-[12px] text-white/55 mt-1 truncate">{v.channel?.name || '—'}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {tab === 'artists' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {subs.length === 0 && <div className="col-span-full"><EmptyState icon={Radio} label="Not following anyone yet." cta="Follow artists in the reel to hear their new drops first." to="/artists" btn="Browse artists" /></div>}
              {subs.map(ch => (
                <div key={ch.id} className="card-shell p-4 text-center" data-testid={`lp-artist-${ch.id}`}>
                  <Link to={`/channel/${ch.id}`}>
                    {ch.avatar ? <img src={resolveFileUrl(ch.avatar)} className="w-20 h-20 rounded-full mx-auto object-cover" alt={ch.name} /> : <div className="w-20 h-20 rounded-full mx-auto" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }} />}
                    <div className="font-display text-[15px] mt-3 truncate">{ch.name}</div>
                  </Link>
                  <button onClick={() => unfollow(ch)} className="btn-ghost mt-3 text-[11.5px] justify-center w-full" data-testid={`lp-unfollow-${ch.id}`}><UserCheck className="w-3.5 h-3.5" /> Following</button>
                </div>
              ))}
            </div>
          )}
          {tab === 'saved' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {saved.length === 0 && <div className="col-span-full"><EmptyState icon={Library} label="Nothing saved for later." cta="Use Watch Later on any video to bookmark it." to="/feed" btn="Browse reel" /></div>}
              {saved.map(v => (
                <Link key={v.id} to={`/watch/${v.id}`} className="card-shell p-0 overflow-hidden hover-lift">
                  <div className="aspect-video bg-white/5">{v.thumb && <img src={resolveFileUrl(v.thumb)} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="p-3"><div className="font-semibold text-[14px] line-clamp-2">{v.title}</div></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Ic, label, cta, to, btn }) {
  return (
    <div className="card-shell p-10 text-center">
      <Ic className="w-8 h-8 mx-auto text-white/30" />
      <div className="font-display text-[18px] mt-3">{label}</div>
      <div className="text-white/55 text-[13px] mt-1 max-w-[360px] mx-auto">{cta}</div>
      <Link to={to} className="btn-primary mt-4 inline-flex">{btn}</Link>
    </div>
  );
}
